import React, { useRef } from 'react';
import { useQuery } from 'react-query';
import { useSelector } from 'react-redux';
import { fetchChatTimerConfig } from 'api/platformConfig';
import { SIMPLR_BOT, CUSTOMER } from 'constants/sources';
import { extractAssignedTicketsArray } from 'extractFromState';
import ChatTab from '../ChatTab/ChatTab';
import { isDelayedAsyncChat, isLiveChat } from 'util/chat';
import { Container, StatsWrapper } from './TicketsInProgress.styles';
import { isValidPartnerReachOutTicket, hasSimplrAgentActionedToReachOut } from 'util/ticketSelector';

const REFETCH_INTERVAL_MS = 1000 * 60 * 5;

const isLastMessageFromCustomer = (lastMessageSource) => {
  return lastMessageSource === CUSTOMER || lastMessageSource === SIMPLR_BOT;
};

const sortChatsByMostRecent = (a, b) => {
  if (a.conversationUpdatedAt >= b.conversationUpdatedAt) {
    return -1;
  }

  return 1;
};

const sortChatTickets = (ticketsList) => {
  const pendingPartnerReachOutTickets = ticketsList
    .filter((ticket) => isValidPartnerReachOutTicket(ticket) && !hasSimplrAgentActionedToReachOut(ticket))
    .sort(sortChatsByMostRecent);
  // For now, the following hides the avatars for delayed messages.
  const syncNonReachOutChats = ticketsList.filter(
    (ticket) =>
      isLiveChat(ticket.communicationType) && !isDelayedAsyncChat(ticket) && !isValidPartnerReachOutTicket(ticket)
  );

  const answeredChats = syncNonReachOutChats
    .filter((ticket) => !isLastMessageFromCustomer(ticket.lastMessageSource))
    .sort(sortChatsByMostRecent);
  const unansweredChats = syncNonReachOutChats
    .filter((ticket) => isLastMessageFromCustomer(ticket.lastMessageSource))
    .sort(sortChatsByMostRecent);

  return [...pendingPartnerReachOutTickets, ...unansweredChats, ...answeredChats];
};

const TicketsInProgress = () => {
  const allAssignedTickets = useSelector(extractAssignedTicketsArray);

  const query = useQuery('chatTimeoutConfig', fetchChatTimerConfig, {
    refetchOnWindowFocus: false,
    refetchInterval: REFETCH_INTERVAL_MS,
    refetchIntervalInBackground: true,
  });

  // Pass the container ref to the chat tabs to keep track of their scroll positions.
  const containerRef = useRef(null);

  if (!Array.isArray(allAssignedTickets) || !allAssignedTickets.length) {
    return null;
  }

  const sortedList = sortChatTickets(allAssignedTickets);

  return (
    <Container ref={containerRef}>
      <StatsWrapper>
        {sortedList.map((ticket) => (
          <ChatTab key={ticket.ticketId} containerRef={containerRef} ticket={ticket} timeoutPeriod={query.data} />
        ))}
      </StatsWrapper>
    </Container>
  );
};

export default TicketsInProgress;
